import { useSelector, useDispatch } from 'react-redux';

import DataGrid from 'app/components/DataGrid';
import Thumbnail from 'app/components/DataGrid';
import classes from 'app/components/DataGrid/classes.module.scss';
import useAccounts from 'app/hooks/useAccounts';
import AccountModal from './AccountModal';

import {
  accountTypeOptions,
  momentOrderOptions,
  featureFlagOptions
} from 'app/utils/options';

import {
  me
} from 'shared/actions/userActions';

const Accounts = () => {
  const account = useSelector(state => state.portalReducer.account);
  const dispatch = useDispatch();

  const onAccountUpdate = record => {
    // if the current account was updated then refresh the session
    if (record.id === account.id) {
      dispatch(me());
    }
  }

  const {
    resource,
    formIsOpen,
    currentRecord,
    onFormClose,
    onAdd,
    onEdit,
    onQuickAdd,
    onQuickEdit,
    onDataGridLoad,
    onFormSubmit,
    onDestroy,
    showFilters,
    getRowData,
    updateRow
  } = useAccounts({
    onUpdate: onAccountUpdate
  });

  const header = 'Accounts';
  const addButtonLabel = 'Add Account';

  const columnOrder = [
    'logo_url',
    'id',
    'name',
    'createdAt',
    'accountType',
    'admin_email',
    'moment_order'
  ];

  const visibleColumns = columnOrder;

  const columnDefOverrides = [
    {
      field: 'logo_url',
      headerName: 'Logo',
      pinned: 'left',
      thumbnail: data => data && data.logo_url,
      valueFormatter: value => ''
    },
    {
      field: 'id',
      headerName: 'ID',
      pinned: 'left'
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      minWidth: 20 * 16, // 20rem
      inputType: 'date',
      renderFields: [
        { field: 'createdAt', type: 'date' },
        { field: 'updatedAt', type: 'date', dateStyle: 'short', prefix: 'Updated ' }
      ]
    },
    {
      field: 'name',
      minWidth: 20 * 16, // 20rem
      quickEdit: true
    },
    {
      field: 'accountType',
      headerName: 'Account Type',
      valueFormatter: value => accountTypeOptions.find(opt => opt.value === value)?.label || 'Custom',
      inputType: 'select',
      inputOptions: accountTypeOptions
    },
    {
      field: 'moment_order',
      headerName: 'Moment Order',
      valueFormatter: value => momentOrderOptions.find(opt => opt.value === value)?.label,
      inputType: 'select',
      inputOptions: momentOrderOptions
    },
    {
      field: 'feature_flags',
      headerName: 'Feature Flags',
      valueFormatter: value => featureFlagOptions.find(opt => opt.value === value)?.label,
      inputType: 'select',
      inputOptions: featureFlagOptions,
      cellRenderer: params => {
        let data = params.data;

        if (data && data.feature_flags) {
          let key = `${data.id}-feature_flags`;

          let listItems = Object.keys(data.feature_flags)
            .filter(flag => data.feature_flags[flag])
            .map(flag => (
              <li key={key + flag} className={classes.cellListItem}>
                {flag}
              </li>
            ));

          return (
            <div key={key} className={classes.cell}>
              <ul className={classes.cellList}>
                {listItems}
              </ul>
            </div>
          );
        }
        return '';
      }
    }
  ]

  const callback = selectedRows => {
    console.log(selectedRows);
  }

  const actions = [
    { key: 'destroy', label: 'Delete Account', callback }
  ]

  return (
    <div>
      <DataGrid
        resource={resource}
        header={header}
        getRowData={getRowData}
        addButtonLabel={addButtonLabel}
        onAdd={onAdd}
        showEdit={true}
        onEdit={onEdit}
        showQuickEdit={true}
        onQuickAdd={onQuickAdd}
        onQuickEdit={onQuickEdit}
        columnOrder={columnOrder}
        columnDefOverrides={columnDefOverrides}
        visibleColumns={visibleColumns}
        showFilters={showFilters}
        showSelect={true}
        actions={actions}
        loadingMessage="Loading accounts..."
        onVisibleColumnsUpdate={columns => { console.log(columns) }}
        onDataGridLoad={onDataGridLoad}
      />
      <AccountModal
        isOpen={formIsOpen}
        onClose={onFormClose}
        account={currentRecord}
        onFormSubmit={onFormSubmit}
        onDestroy={onDestroy}
        updateRow={updateRow}
      />
    </div>
  );
}

export default Accounts;
